import { type FC, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { DialogAction } from './DialogAction'
import type { DialogCancelActionProps } from './types'
import { useDialogContext } from './useDialogContext'

/**
 * CancelAction component (cancel button)
 */
export const DialogCancelAction: FC<DialogCancelActionProps> = ({
  label,
  onClick: customOnClick,
  isDefault = false,
  ...restProps
}) => {
  const { onClose, registerCancelButton } = useDialogContext()
  const { t } = useTranslation()
  const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement | null>(null)

  const setButtonRef = useCallback(
    (element: HTMLButtonElement | HTMLAnchorElement | null) => {
      buttonRef.current = element
      if (element instanceof HTMLButtonElement) {
        registerCancelButton?.(element)
      }
    },
    [registerCancelButton],
  )

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (customOnClick) {
        customOnClick(event)
      }
      onClose()
    },
    [customOnClick, onClose],
  )

  return (
    <DialogAction
      ref={setButtonRef}
      intent="neutral"
      label={label ?? t('common.cancel')}
      onClick={handleClick}
      isDefault={isDefault}
      {...restProps}
    />
  )
}

DialogCancelAction.displayName = 'DialogCancelAction'
